import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Search, ShoppingCart, Plus, Minus, X, CheckCircle, Package } from 'lucide-react';
import OrderSummaryBar, { CategoryChips } from '../components/OrderSummaryBar';
import ProductDetailsModal from '../components/ProductDetailsModal';
import SideCart from '../components/SideCart';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtBRL = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(v) || 0);

export default function VendedorPedido() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [catalogo, setCatalogo] = useState([]);
  const [cart, setCart] = useState({});
  const [search, setSearch] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [observacao, setObservacao] = useState('');
  const [showCart, setShowCart] = useState(false);
  const [loading, setLoading] = useState(true);
  const [clientesLista, setClientesLista] = useState([]);
  const [clienteSelecionado, setClienteSelecionado] = useState('');
  const [categoriaFiltro, setCategoriaFiltro] = useState('');
  const [valorMinimo, setValorMinimo] = useState(0);
  const [detailProduct, setDetailProduct] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== 'vendedor') { navigate('/login'); return; }
    (async () => {
      try {
        const [cat, cli, minOrder] = await Promise.all([
          axios.get(`${API}/vendedor/me/catalogo`),
          axios.get(`${API}/vendedor/me/clientes`).catch(() => ({ data: { meus: [], outros: [] } })),
          axios.get(`${API}/vendedor/me/min-order`).catch(() => ({ data: { valor_minimo: 0 } })),
        ]);
        setCatalogo(cat.data);
        const merged = [...(cli.data?.meus || []), ...(cli.data?.outros || [])];
        setClientesLista(merged);
        setValorMinimo(Number(minOrder.data?.valor_minimo) || 0);
      } catch (e) { toast.error('Erro ao carregar catálogo'); }
      setLoading(false);
    })();
  }, [authLoading, user, navigate]);

  // Categorias únicas ordenadas por frequência
  const categorias = useMemo(() => {
    const count = {};
    catalogo.forEach(p => {
      const c = (p.categoria || '').trim();
      if (c) count[c] = (count[c] || 0) + 1;
    });
    return Object.entries(count).sort((a, b) => b[1] - a[1]).map(([c]) => c);
  }, [catalogo]);

  const filtered = useMemo(() => {
    const s = search.toLowerCase().trim();
    return catalogo.filter(p => {
      if (categoriaFiltro && (p.categoria || '') !== categoriaFiltro) return false;
      if (!s) return true;
      return (
        (p.produto || '').toLowerCase().includes(s) ||
        (p.codigo || '').toLowerCase().includes(s) ||
        (p.categoria || '').toLowerCase().includes(s)
      );
    });
  }, [catalogo, search, categoriaFiltro]);

  const total = Object.entries(cart).reduce((s, [, item]) => s + (item.qty * item.valor_unitario), 0);
  const itemsCount = Object.values(cart).reduce((s, item) => s + item.qty, 0);
  const atingiuMinimo = valorMinimo <= 0 || total >= valorMinimo;

  const addToCart = (p, opts = {}) => {
    const valor = p.preco_unitario_venda || p.valor_por_unidade || p.preco_unitario_referencia || 0;
    const foto = (p.fotos && p.fotos[0]) || null;
    setCart(prev => {
      const cur = prev[p.id] || {
        qty: 0, valor_unitario: valor,
        produto: p.produto, produto_nome: p.produto,
        unidade_venda: p.unidade_venda,
        foto, cor: opts.cor || '', tamanho: opts.tamanho || '',
      };
      return {
        ...prev,
        [p.id]: {
          ...cur,
          qty: cur.qty + 1,
          cor: opts.cor || cur.cor || '',
          tamanho: opts.tamanho || cur.tamanho || '',
        },
      };
    });
  };
  const removeItem = (pid) => setCart(prev => { const { [pid]: _rm, ...rest } = prev; return rest; });
  const decreaseItem = (pid) => setCart(prev => {
    const cur = prev[pid];
    if (!cur) return prev;
    if (cur.qty <= 1) { const { [pid]: _, ...rest } = prev; return rest; }
    return { ...prev, [pid]: { ...cur, qty: cur.qty - 1 } };
  });
  const setQty = (pid, qty) => {
    const n = Math.max(0, parseInt(qty || '0', 10));
    if (n === 0) setCart(prev => { const { [pid]: _, ...rest } = prev; return rest; });
    else setCart(prev => ({ ...prev, [pid]: { ...prev[pid], qty: n } }));
  };

  const handleSubmit = async () => {
    const items = Object.entries(cart).map(([pid, item]) => ({
      produto_id: pid,
      produto_nome: item.produto_nome,
      quantidade: item.qty,
      valor_unitario: item.valor_unitario,
      unidade_venda: item.unidade_venda,
      cor: item.cor || null,
      tamanho: item.tamanho || null,
    }));
    if (items.length === 0) { toast.error('Adicione pelo menos um produto'); return; }
    if (!clienteSelecionado) { toast.error('Selecione o cliente do pedido'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/vendedor/me/pedido`, {
        items,
        observacao,
        cliente_nome: clienteSelecionado,
      });
      toast.success(`Pedido #${res.data.numero_pedido} enviado ao admin!`, { duration: 6000 });
      setCart({});
      setObservacao('');
      setClienteSelecionado('');
      navigate('/vendedor/dashboard');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao enviar pedido');
    }
    setSubmitting(false);
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="portal-page-bg min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-900 dark:to-gray-800 pb-24">
      <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700 sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
          <h1 className="flex-1 font-bold text-gray-900 dark:text-white">Gerar Pedido</h1>
          <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700 font-semibold">Vendedor: {user?.name}</span>
          <button onClick={() => setShowCart(true)} className="relative p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg" data-testid="vendedor-open-cart">
            <ShoppingCart className="w-5 h-5" />
            {itemsCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-blue-600 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">{itemsCount}</span>
            )}
          </button>
        </div>

        <div className="px-4 py-2 border-t border-gray-100 dark:border-gray-700 grid grid-cols-1 md:grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] font-semibold text-gray-500 uppercase">Cliente *</label>
            <select
              value={clienteSelecionado}
              onChange={(e) => setClienteSelecionado(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900"
              data-testid="vendedor-cliente-select"
            >
              <option value="">Selecione o cliente...</option>
              {clientesLista.map(c => (
                <option key={c.id || c.nome} value={c.nome}>{c.nome}</option>
              ))}
            </select>
          </div>
          <div className="relative">
            <label className="text-[10px] font-semibold text-gray-500 uppercase">Buscar produto</label>
            <Search className="absolute left-3 top-8 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Nome, código, categoria..."
              className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900"
              data-testid="vendedor-catalog-search"
            />
          </div>
        </div>
        {/* Filtro por categoria */}
        {categorias.length > 0 && (
          <div className="px-4 py-2 border-t border-gray-100 dark:border-gray-700">
            <CategoryChips
              categorias={categorias}
              selected={categoriaFiltro}
              onSelect={setCategoriaFiltro}
              color="blue"
              testidPrefix="vendedor-category"
            />
          </div>
        )}
      </header>

      <div className="max-w-7xl mx-auto px-3 py-4 flex gap-4 items-start">
        <div className="flex-1 min-w-0">
        {filtered.length === 0 ? (
          <p className="text-center text-sm text-gray-500 py-8">Nenhum produto encontrado.</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="vendedor-catalog-grid">
            {filtered.map(p => {
              const foto = (p.fotos && p.fotos[0]) || null;
              const inCart = cart[p.id]?.qty || 0;
              const valor = p.preco_unitario_venda || p.valor_por_unidade || p.preco_unitario_referencia || 0;
              return (
                <div
                  key={p.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => setDetailProduct(p)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setDetailProduct(p); }}
                  className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden flex flex-col cursor-pointer hover:shadow-lg hover:border-blue-300 transition"
                  data-testid={`vendedor-product-${p.id}`}
                  title="Clique para ver detalhes do produto"
                >
                  <div className="aspect-square bg-gray-100 dark:bg-gray-900 relative w-full">
                    {foto ? (
                      <img src={foto} alt={p.produto} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400">
                        <Package className="w-12 h-12" />
                      </div>
                    )}
                  </div>
                  <div className="p-3 flex-1 flex flex-col" onClick={(e) => e.stopPropagation()}>
                    <p className="text-[10px] text-gray-500 uppercase">{p.codigo}</p>
                    <button
                      type="button"
                      onClick={() => setDetailProduct(p)}
                      className="text-sm font-semibold text-gray-900 dark:text-white line-clamp-2 mb-1 text-left hover:text-blue-600 transition"
                    >
                      {p.produto}
                    </button>
                    <p className="text-lg font-bold text-blue-600 mt-auto">{fmtBRL(valor)}</p>
                    <p className="text-[10px] text-gray-500">por {p.unidade_venda || 'unidade'}{p.quantidade_por_unidade > 1 ? ` (${p.quantidade_por_unidade} un)` : ''}</p>

                    {inCart === 0 ? (
                      <button
                        onClick={() => addToCart(p)}
                        className="mt-2 w-full bg-blue-600 hover:bg-blue-700 text-white py-1.5 rounded-lg text-xs font-semibold flex items-center justify-center gap-1"
                        data-testid={`vendedor-add-${p.id}`}
                      >
                        <Plus className="w-3.5 h-3.5" /> Adicionar
                      </button>
                    ) : (
                      <div className="mt-2 flex items-center gap-1">
                        <button onClick={() => decreaseItem(p.id)} className="bg-gray-200 dark:bg-gray-700 p-1.5 rounded-lg">
                          <Minus className="w-3.5 h-3.5" />
                        </button>
                        <input
                          type="number"
                          min="0"
                          value={inCart}
                          onChange={(e) => setQty(p.id, e.target.value)}
                          className="flex-1 text-center text-sm border border-gray-300 dark:border-gray-600 rounded-lg py-1 bg-white dark:bg-gray-900"
                        />
                        <button onClick={() => addToCart(p)} className="bg-blue-600 hover:bg-blue-700 text-white p-1.5 rounded-lg">
                          <Plus className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        </div>

        {/* Side Cart (desktop lg+) */}
        <SideCart
          cart={cart}
          onRemove={removeItem}
          onDec={decreaseItem}
          onInc={(pid) => addToCart(catalogo.find(p => p.id === pid) || { id: pid })}
          onCheckout={() => setShowCart(true)}
          total={total}
          itemsCount={itemsCount}
          valorMinimo={valorMinimo}
          color="blue"
          testidPrefix="vendedor-side-cart"
          disabled={!clienteSelecionado}
        />
      </div>

      {/* Cart drawer */}
      {showCart && (
        <div className="fixed inset-0 z-50 bg-black/50 flex justify-end" onClick={() => setShowCart(false)}>
          <div className="w-full max-w-md bg-white dark:bg-gray-800 h-full overflow-y-auto p-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-bold text-lg">Carrinho ({itemsCount})</h2>
              <button onClick={() => setShowCart(false)} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            {Object.keys(cart).length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-8">Carrinho vazio</p>
            ) : (
              <>
                <div className="space-y-2 mb-4">
                  {Object.entries(cart).map(([pid, item]) => (
                    <div key={pid} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 flex items-center justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold truncate">{item.produto_nome}</p>
                        <p className="text-xs text-gray-500">{fmtBRL(item.valor_unitario)} × {item.qty}</p>
                      </div>
                      <p className="font-bold text-blue-600">{fmtBRL(item.qty * item.valor_unitario)}</p>
                    </div>
                  ))}
                </div>
                <textarea
                  value={observacao}
                  onChange={(e) => setObservacao(e.target.value)}
                  placeholder="Observação para o admin..."
                  rows="3"
                  className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded-lg p-2 bg-white dark:bg-gray-900 mb-3"
                  data-testid="vendedor-pedido-obs"
                />
                <div className="text-right font-bold text-xl mb-3">Total: {fmtBRL(total)}</div>
                {valorMinimo > 0 && !atingiuMinimo && (
                  <div className="mb-3 p-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg text-xs text-orange-800 dark:text-orange-200" data-testid="vendedor-cart-min-warning">
                    ⚠️ Faltam <b>{fmtBRL(valorMinimo - total)}</b> para atingir o pedido mínimo de <b>{fmtBRL(valorMinimo)}</b>.
                  </div>
                )}
                <button
                  onClick={handleSubmit}
                  disabled={submitting || !clienteSelecionado || !atingiuMinimo}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="vendedor-submit-pedido"
                >
                  {submitting ? 'Enviando...' : <><CheckCircle className="w-5 h-5" /> Enviar ao Admin</>}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Modal de detalhes do produto */}
      {detailProduct && (
        <ProductDetailsModal
          product={detailProduct}
          onClose={() => setDetailProduct(null)}
          onAdd={addToCart}
          color="blue"
          showPrice
        />
      )}

      {/* Barra flutuante com progresso do pedido mínimo */}
      {!showCart && (
        <OrderSummaryBar
          itemsCount={itemsCount}
          total={total}
          valorMinimo={valorMinimo}
          onOpenCart={() => setShowCart(true)}
          color="blue"
          testidPrefix="vendedor-summary"
        />
      )}
    </div>
  );
}
